import { useState } from 'react';
import { Download, Calendar, Users, Anchor, Clock, BarChart2, Ship, ArrowUpRight, FileText, Database, Filter } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';

export default function Report() {
  const [timeframe, setTimeframe] = useState('monthly');
  const [isExporting, setIsExporting] = useState(false);
  const [projectFilter, setProjectFilter] = useState('All Projects');
  const [departmentFilter, setDepartmentFilter] = useState('All Departments');
  const [specificProject, setSpecificProject] = useState('All Projects');

  const handleExport = async (type) => {
    setIsExporting(true);
    try {
      if (type === 'PDF') {
        const { jsPDF } = await import('jspdf');
        const autoTable = (await import('jspdf-autotable')).default;
        const doc = new jsPDF();
        doc.setFontSize(10);
        doc.text('Shipyard Project Management Report', 10, 10);
        doc.setFontSize(8);
        doc.text('Performance Overview and Analytics Dashboard', 10, 15);
        doc.text('Generated: April 8, 2025', 10, 20);
        doc.text('Data updated: 2 hours ago', 10, 25);
        doc.text('Department: Shipyard Operations', 10, 30);
        doc.text('Project Status Overview', 10, 35);
        autoTable(doc, {
          startY: 40,
          head: [['Project ID', 'Name', 'Progress', 'Status']],
          body: filteredProjects.map(project => [
            project.id, project.name, `${project.progress}%`, project.status
          ]),
          theme: 'grid',
          styles: { fontSize: 8, fillColor: [41, 128, 185] },
          headStyles: { fillColor: [52, 73, 94] },
          alternateRowStyles: { fillColor: [241, 196, 15] },
          margin: { top: 40, bottom: 10 }
        });
        doc.save('report.pdf');
      } else if (type === 'Word') {
        const { Document, Packer, Paragraph, TextRun, Table, TableRow, TableCell } = await import('docx');
        const doc = new Document({
          sections: [{
            children: [
              new Paragraph({
                children: [
                  new TextRun('Shipyard Project Management Report'),
                  new TextRun({ text: 'Performance Overview and Analytics Dashboard', break: 1 }),
                  new TextRun({ text: 'Generated: April 8, 2025', break: 1 }),
                  new TextRun({ text: 'Data updated: 2 hours ago', break: 1 }),
                  new TextRun({ text: 'Department: Shipyard Operations', break: 1 }),
                  new TextRun({ text: 'Project Status Overview', break: 2 })
                ]
              }),
              new Table({
                rows: [
                  new TableRow({
                    children: [
                      new TableCell({ children: [new Paragraph('Project ID')] }),
                      new TableCell({ children: [new Paragraph('Name')] }),
                      new TableCell({ children: [new Paragraph('Progress')] }),
                      new TableCell({ children: [new Paragraph('Status')] })
                    ]
                  }),
                  ...filteredProjects.map(project => new TableRow({
                    children: [
                      new TableCell({ children: [new Paragraph(project.id)] }),
                      new TableCell({ children: [new Paragraph(project.name)] }),
                      new TableCell({ children: [new Paragraph(`${project.progress}%`)] }),
                      new TableCell({ children: [new Paragraph(project.status)] })
                    ]
                  }))
                ]
              })
            ]
          }]
        });
        const blob = await Packer.toBlob(doc);
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'report.docx';
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Export error:', error);
    } finally {
      setIsExporting(false);
    }
  };

  // Sample data for the report
  const projectData = {
    completion: 68,
    onSchedule: 82,
    projectsActive: 14,
    delayedProjects: 3,
    totalWorkers: 248,
    materialCost: '$1.24M',
    laborHours: '5,840',
    upcomingDeliveries: 5
  };

  // Sample data for chart
  const monthlyProgress = [
    { month: 'Jan', progress: 75 },
    { month: 'Feb', progress: 82 },
    { month: 'Mar', progress: 68 },
    { month: 'Apr', progress: 72 },
    { month: 'May', progress: 80 },
    { month: 'Jun', progress: 65 }
  ];

  // Sample projects
  const projects = [
    { id: 'SY-2134', name: 'Cargo Ship Maintenance', progress: 75, status: 'On Track' },
    { id: 'SY-1892', name: 'Offshore Platform Assembly', progress: 32, status: 'Delayed' },
    { id: 'SY-2201', name: 'Commercial Vessel Repair', progress: 91, status: 'On Track' },
    { id: 'SY-1974', name: 'Naval Transport Construction', progress: 52, status: 'On Track' },
    { id: 'SY-2045', name: 'Oil Tanker Inspection', progress: 28, status: 'Delayed' }
  ];

  // Filtered projects based on selected filters
  const filteredProjects = projects.filter(project => {
    const projectMatch = projectFilter === 'All Projects' ||
      (projectFilter === 'Active Projects' && project.status === 'On Track') ||
      (projectFilter === 'Completed Projects' && project.progress === 100) ||
      (projectFilter === 'Delayed Projects' && project.status === 'Delayed');

    const departmentMatch = departmentFilter === 'All Departments' ||
      (departmentFilter === 'Construction' && project.name.includes('Construction')) ||
      (departmentFilter === 'Maintenance' && project.name.includes('Maintenance')) ||
      (departmentFilter === 'Inspection' && project.name.includes('Inspection'));

    const specificProjectMatch = specificProject === 'All Projects' || project.id === specificProject;

    return projectMatch && departmentMatch && specificProjectMatch;
  });

  // Update monthlyProgress based on timeframe
  const getProgressData = () => {
    switch (timeframe) {
      case 'weekly':
        return [
          { week: 'Week 1', progress: 70 },
          { week: 'Week 2', progress: 75 },
          { week: 'Week 3', progress: 80 },
          { week: 'Week 4', progress: 85 }
        ];
      case 'quarterly':
        return [
          { quarter: 'Q1', progress: 78 },
          { quarter: 'Q2', progress: 82 },
          { quarter: 'Q3', progress: 85 },
          { quarter: 'Q4', progress: 88 }
        ];
      default:
        return monthlyProgress;
    }
  };

  const progressData = getProgressData();

  return (
    <div className="bg-gray-50 min-h-screen">
      {/* Header */}
      <header className="bg-gradient-to-r from-blue-800 to-blue-600 text-white shadow-lg">
        <div className="container mx-auto py-8 px-4">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold flex items-center">
                <Ship className="mr-3" />
                Shipyard Project Management Report
              </h1>
              <p className="mt-2 text-blue-100">Performance Overview and Analytics Dashboard</p>
            </div>
            <div className="flex space-x-4">
              <button 
                onClick={() => handleExport('PDF')}
                disabled={isExporting}
                className="bg-white text-blue-800 px-4 py-2 rounded-lg flex items-center shadow-md hover:bg-blue-50 transition-all duration-200"
              >
                <Download size={18} className="mr-2" />
                Export PDF
              </button>
              <button 
                onClick={() => handleExport('Word')}
                disabled={isExporting}
                className="bg-white text-blue-800 px-4 py-2 rounded-lg flex items-center shadow-md hover:bg-blue-50 transition-all duration-200"
              >
                <FileText size={18} className="mr-2" />
                Export Word
              </button>
            </div>
          </div>
          
          {/* Report Info */}
          <div className="mt-8 flex flex-wrap items-center text-sm text-blue-100">
            <div className="flex items-center mr-6">
              <Calendar size={16} className="mr-1" />
              Generated: April 8, 2025
            </div>
            <div className="flex items-center mr-6">
              <Database size={16} className="mr-1" />
              Data updated: 2 hours ago
            </div>
            <div className="flex items-center">
              <Users size={16} className="mr-1" />
              Department: Shipyard Operations
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto py-8 px-4">
        {/* Filter Controls */}
        <div className="bg-white p-4 mb-6 rounded-lg shadow flex justify-between items-center">
          <div className="flex items-center">
            <Filter size={18} className="text-gray-500 mr-2" />
            <span className="text-gray-700 font-medium">Filter Report:</span>
          </div>
          <div className="flex space-x-2">
            <select 
              className="border border-gray-300 rounded px-3 py-1 text-gray-700"
              value={projectFilter}
              onChange={(e) => setProjectFilter(e.target.value)}
            >
              <option>All Projects</option>
              <option>Active Projects</option>
              <option>Completed Projects</option>
              <option>Delayed Projects</option>
            </select>
            <select 
              className="border border-gray-300 rounded px-3 py-1 text-gray-700"
              value={departmentFilter}
              onChange={(e) => setDepartmentFilter(e.target.value)}
            >
              <option>All Departments</option>
              <option>Construction</option>
              <option>Maintenance</option>
              <option>Inspection</option>
            </select>
            <select 
              className="border border-gray-300 rounded px-3 py-1 text-gray-700"
              value={specificProject}
              onChange={(e) => setSpecificProject(e.target.value)}
            >
              <option>All Projects</option>
              {projects.map(project => (
                <option key={project.id} value={project.id}>{project.name}</option>
              ))}
            </select>
            <div className="flex border border-gray-300 rounded overflow-hidden">
              <button 
                className={`px-3 py-1 ${timeframe === 'weekly' ? 'bg-blue-100 text-blue-800' : 'bg-white text-gray-700'}`}
                onClick={() => setTimeframe('weekly')}
              >
                Weekly
              </button>
              <button 
                className={`px-3 py-1 ${timeframe === 'monthly' ? 'bg-blue-100 text-blue-800' : 'bg-white text-gray-700'}`}
                onClick={() => setTimeframe('monthly')}
              >
                Monthly
              </button>
              <button 
                className={`px-3 py-1 ${timeframe === 'quarterly' ? 'bg-blue-100 text-blue-800' : 'bg-white text-gray-700'}`}
                onClick={() => setTimeframe('quarterly')}
              >
                Quarterly
              </button>
            </div>
          </div>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow p-6 border-l-4 border-blue-500">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-gray-500 text-sm">Completion Rate</p>
                <h3 className="text-3xl font-bold text-gray-800">{projectData.completion}%</h3>
              </div>
              <div className="bg-blue-100 p-3 rounded-lg">
                <BarChart2 size={24} className="text-blue-500" />
              </div>
            </div>
            <div className="mt-4">
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div className="bg-blue-500 h-2 rounded-full" style={{ width: `${projectData.completion}%` }}></div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6 border-l-4 border-green-500">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-gray-500 text-sm">On Schedule Rate</p>
                <h3 className="text-3xl font-bold text-gray-800">{projectData.onSchedule}%</h3>
              </div>
              <div className="bg-green-100 p-3 rounded-lg">
                <Clock size={24} className="text-green-500" />
              </div>
            </div>
            <p className="text-sm text-green-600 mt-4 flex items-center">
              <ArrowUpRight size={16} className="mr-1" />
              5% increase from last month
            </p>
          </div>

          <div className="bg-white rounded-lg shadow p-6 border-l-4 border-purple-500">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-gray-500 text-sm">Active Projects</p>
                <h3 className="text-3xl font-bold text-gray-800">{projectData.projectsActive}</h3>
              </div>
              <div className="bg-purple-100 p-3 rounded-lg">
                <Anchor size={24} className="text-purple-500" />
              </div>
            </div>
            <p className="text-sm text-gray-600 mt-4">
              {projectData.delayedProjects} projects delayed
            </p>
          </div>

          <div className="bg-white rounded-lg shadow p-6 border-l-4 border-orange-500">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-gray-500 text-sm">Workforce</p>
                <h3 className="text-3xl font-bold text-gray-800">{projectData.totalWorkers}</h3>
              </div>
              <div className="bg-orange-100 p-3 rounded-lg">
                <Users size={24} className="text-orange-500" />
              </div>
            </div>
            <p className="text-sm text-gray-600 mt-4">
              Labor Hours: {projectData.laborHours}
            </p>
          </div>
        </div>

        {/* Project Progress Table */}
        <div className="bg-white rounded-lg shadow mb-8">
          <div className="border-b border-gray-200 p-6">
            <h2 className="text-xl font-bold text-gray-800">Project Status Overview</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Project ID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Progress</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredProjects.map((project) => (
                  <tr key={project.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{project.id}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{project.name}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-full bg-gray-200 rounded-full h-2 mr-2">
                          <div 
                            className={`h-2 rounded-full ${project.status === 'Delayed' ? 'bg-red-500' : 'bg-green-500'}`} 
                            style={{ width: `${project.progress}%` }}
                          ></div>
                        </div>
                        <span className="text-sm text-gray-700">{project.progress}%</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        project.status === 'On Track' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {project.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Additional Stats */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Monthly Progress Chart</h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={progressData}>
                  <XAxis dataKey={timeframe === 'weekly' ? 'week' : timeframe === 'quarterly' ? 'quarter' : 'month'} />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="progress" fill="#3B82F6" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Resource Allocation</h3>
            <div className="space-y-4">
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-600">Labor</span>
                  <span className="text-gray-800 font-medium">42%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div className="bg-blue-500 h-2 rounded-full" style={{ width: '42%' }}></div>
                </div>
              </div>
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-600">Materials</span>
                  <span className="text-gray-800 font-medium">30%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div className="bg-green-500 h-2 rounded-full" style={{ width: '30%' }}></div>
                </div>
              </div>
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-600">Equipment</span>
                  <span className="text-gray-800 font-medium">18%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div className="bg-purple-500 h-2 rounded-full" style={{ width: '18%' }}></div>
                </div>
              </div>
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-600">Other</span>
                  <span className="text-gray-800 font-medium">10%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div className="bg-yellow-500 h-2 rounded-full" style={{ width: '10%' }}></div>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Key Metrics</h3>
            <div className="space-y-4">
              <div className="flex justify-between border-b border-gray-100 pb-2">
                <span className="text-gray-600">Material Cost</span>
                <span className="font-medium">{projectData.materialCost}</span>
              </div>
              <div className="flex justify-between border-b border-gray-100 pb-2">
                <span className="text-gray-600">Upcoming Deliveries</span>
                <span className="font-medium">{projectData.upcomingDeliveries}</span>
              </div>
              <div className="flex justify-between border-b border-gray-100 pb-2">
                <span className="text-gray-600">Safety Incidents</span>
                <span className="font-medium">0</span>
              </div>
              <div className="flex justify-between border-b border-gray-100 pb-2">
                <span className="text-gray-600">Quality Inspections Passed</span>
                <span className="font-medium">12/14</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Avg. Project Duration</span>
                <span className="font-medium">72 days</span>
              </div>
            </div>
          </div>
        </div>

        {/* Risk Assessments */}
        <div className="bg-white rounded-lg shadow p-6 mb-8">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Risk Assessment Summary</h3>
          <div className="space-y-4">
            <div className="flex items-center p-3 bg-yellow-50 border-l-4 border-yellow-400 rounded">
              <div className="text-yellow-800">
                <p className="font-medium">Supply Chain Disruption</p>
                <p className="text-sm">Potential delay in receiving critical components for SY-1892.</p>
              </div>
            </div>
            <div className="flex items-center p-3 bg-red-50 border-l-4 border-red-400 rounded">
              <div className="text-red-800">
                <p className="font-medium">Resource Shortage</p>
                <p className="text-sm">Skilled welder shortage affecting the Offshore Platform Assembly project.</p>
              </div>
            </div>
            <div className="flex items-center p-3 bg-green-50 border-l-4 border-green-400 rounded">
              <div className="text-green-800">
                <p className="font-medium">Weather Conditions</p>
                <p className="text-sm">Favorable forecast for the next 2 weeks. No expected weather delays.</p>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Export Loading Overlay */}
      {isExporting && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg flex flex-col items-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            <p className="mt-4 text-gray-700">Preparing your document...</p>
          </div>
        </div>
      )}
    </div>
  );
}